import type { World } from '@kdk/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { combine2, useAsyncState, type AsyncState } from '../shared-ui/asyncState';
import { api } from './api';

export const queryKeys = {
  cases: ['cases'],
  staff: ['staff'],
  tasks: ['tasks'],
  schedule: ['schedule'],
  reflections: ['reflections'],
  accounts: ['accounts'],
} as const;

export const useCases = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.cases, queryFn: () => api.getCases() }));
export const useStaff = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.staff, queryFn: () => api.getStaff() }));
export const useTasks = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.tasks, queryFn: () => api.getTasks() }));
export const useSchedule = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.schedule, queryFn: () => api.getSchedule() }));
export const useReflections = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.reflections, queryFn: () => api.getReflections() }));

export const useAccounts = () =>
  useAsyncState(useQuery({ queryKey: queryKeys.accounts, queryFn: () => api.getAccounts() }));

/** 案件 + 員工(ほとんどのページが両方を使う)。 */
export const useWorld = (): AsyncState<World> => {
  const cases = useCases();
  const staff = useStaff();
  return useMemo(
    () => combine2(cases, staff, (c, s): World => ({ cases: c, staff: s })),
    [cases, staff],
  );
};

/**
 * 書き込みのあと、関係するデータを取り直す。表示中のものはすぐ再取得、
 * 表示していないページのものは「古い」扱いになり、開いたときに取り直される。
 */
export const useInvalidate = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (...keys: readonly (keyof typeof queryKeys)[]): Promise<void> => {
      await Promise.all(
        keys.map((key) => queryClient.invalidateQueries({ queryKey: queryKeys[key] })),
      );
    },
    [queryClient],
  );
};
